import { getWelfareVariant } from "../../welfareVariantState";

/**
 * Les vidéos de « Bien démarrer » : une vidéo « Comprendre » en tête de chaque
 * thème, et un tuto vidéo par leçon du thème « Aller plus loin » (la vidéo et
 * sa leçon pas à pas ne font qu'une carte, voir `onboardingAcademy.js`).
 *
 * Elles sont hébergées sur Vimeo (dossier « AKONGO TUTO »), masquées de Vimeo
 * et intégrables partout. Source des identifiants : dépôt akongo-motion-design,
 * `docs/videos-vimeo.json`. Le paramètre `h` est obligatoire : sans lui, une
 * vidéo masquée ne se lit pas une fois intégrée.
 *
 * Chaque vidéo existe en français et en anglais ; les autres langues de
 * l'interface reçoivent la version anglaise. Sa vignette est servie par
 * l'app (`public/img/onboarding-videos/{id}.jpg`) : le carton de titre jaune,
 * image extraite à 3,4 s de l'export du film (dépôt akongo-motion-design).
 * Les vignettes de Vimeo ne sont pas fiables : une vidéo tout juste envoyée
 * garde l'image de 0 s (écran noir), et une image plus ancienne de la même
 * vidéo peut montrer tout autre chose (relevés du 04/10 et du 07/10/2026).
 */
const vimeo = (id, h) => Object.freeze({ id, h });
const video = (id, seconds, fr, en) => Object.freeze({ id, seconds, fr, en });

/** La vidéo « Comprendre » de chaque thème. Le thème « interface » n'en a pas encore. */
export const COURSE_VIDEOS = Object.freeze({
  park: video("park", 106, vimeo("1232815098", "f6ad26f830"), vimeo("1232815097", "8b8b8ed35a")),
  collection: video("collection", 129, vimeo("1232815094", "a46fb5513f"), vimeo("1232815089", "0d6bcc1811")),
  welfare: video("welfare", 119, vimeo("1235353836", "1cbbbe0975"), vimeo("1235353835", "1a07530c87")),
  team: video("team", 116, vimeo("1232815081", "da500789f3"), vimeo("1232815073", "0cf6cd1186")),
});

/** Les tutos vidéo du thème « Aller plus loin », dans l'ordre de la série : l'identifiant est celui de leur leçon. */
export const EXPLORE_VIDEOS = Object.freeze([
  video("sectorLogbook", 104, vimeo("1232815074", "7b230e8aa3"), vimeo("1232815075", "1b9c2e279c")),
  video("dailyMonitoring", 90, vimeo("1232815067", "4dd81c08ae"), vimeo("1232815057", "9ef775e93b")),
  video("parameters", 97, vimeo("1232815055", "02e791ab11"), vimeo("1232815053", "31fc6a47fb")),
  video("multiParameterAnalysis", 76, vimeo("1232815051", "258077687d"), vimeo("1232815044", "5ced9404a1")),
  video("veterinary", 102, vimeo("1232815043", "767eef442d"), vimeo("1232815042", "3fa4d4f05d")),
  video("enrichments", 89, vimeo("1232815041", "862709cee7"), vimeo("1232815034", "303dab82f5")),
  video("training", 91, vimeo("1232815029", "531613b81a"), vimeo("1232815028", "a118698632")),
  video("scans", 95, vimeo("1232815027", "746fb9b503"), vimeo("1232815021", "31fe6965b7")),
  video("focals", 83, vimeo("1232815016", "50ff0c6650"), vimeo("1232815014", "216193402b")),
  video("questionnaires", 93, vimeo("1232815012", "6be543b077"), vimeo("1232815010", "60dbb92540")),
  video("rafiki", 74, vimeo("1232814999", "6a8efc0703"), vimeo("1232814997", "7870ff7742")),
  // Douzième tuto (25/10/2026) : pas encore de version établissement, la même vidéo sert aux deux.
  video("researchProjects", 111, vimeo("1239308347", "2755b861d4"), vimeo("1239309494", "f81668ecfd")),
]);

export const EXPLORE_VIDEO_BY_ID = Object.freeze(Object.fromEntries(EXPLORE_VIDEOS.map((item) => [item.id, item])));

/**
 * Films des sous-thèmes : une leçon complémentaire d'un tuto peut avoir le sien
 * (`moreVideoIds` dans `onboardingAcademy.js`). La pharmacie, sous-thème du tuto
 * « Le vétérinaire » (27/10/2026), pas encore de version établissement.
 */
export const LESSON_VIDEOS = Object.freeze([
  video("pharmacy", 135, vimeo("1239807397", "287095b01a"), vimeo("1239807740", "633357d73b")),
]);

export const LESSON_VIDEO_BY_ID = Object.freeze(Object.fromEntries(LESSON_VIDEOS.map((item) => [item.id, item])));

/**
 * Version établissement des mêmes vidéos, pour un parc de catégorie Laboratoire
 * (issue #1096) : mêmes thèmes, mêmes leçons, tournés dans un établissement
 * (unités, hébergements, animaliers). Sous-dossier « LAB » de « AKONGO TUTO » ;
 * source : dépôt akongo-motion-design, `docs/videos-vimeo-labo.json`.
 */
const LAB_VIDEOS = Object.freeze([
  video("park", 97, vimeo("1233512957", "c0b3041c02"), vimeo("1233512946", "d66ef3cf86")),
  video("collection", 114, vimeo("1233512944", "b95e190a6f"), vimeo("1233512942", "ad3e012ccf")),
  video("welfare", 119, vimeo("1235353890", "1291fa894c"), vimeo("1235353891", "deb5a87e78")),
  video("team", 116, vimeo("1233512925", "397e854cbf"), vimeo("1233512922", "fad6887878")),
  video("sectorLogbook", 108, vimeo("1233512923", "3fb709e552"), vimeo("1233512894", "90ea7a3af4")),
  video("dailyMonitoring", 91, vimeo("1233512893", "15707c534e"), vimeo("1233512890", "5b5fe97988")),
  video("parameters", 108, vimeo("1233512887", "e89462d103"), vimeo("1233512867", "749c499db2")),
  video("multiParameterAnalysis", 90, vimeo("1233512863", "139870d5d7"), vimeo("1233512860", "6cec7137e1")),
  video("veterinary", 106, vimeo("1233512856", "a8bf50a32f"), vimeo("1233512814", "e6786ece52")),
  video("enrichments", 92, vimeo("1233512812", "27b8767dfd"), vimeo("1233512811", "b04475d671")),
  video("training", 91, vimeo("1233512809", "7c910f5c1a"), vimeo("1233512788", "f4c0918b5c")),
  video("scans", 95, vimeo("1233512782", "c5b1a12cbb"), vimeo("1233512781", "baf14dbb92")),
  video("focals", 84, vimeo("1233512783", "5ebb191312"), vimeo("1233512758", "562c6bcb1c")),
  video("questionnaires", 93, vimeo("1233512743", "69bc773df5"), vimeo("1233512744", "b65107953d")),
  video("rafiki", 77, vimeo("1233512742", "ab040c0328"), vimeo("1233512745", "9d8187ea03")),
]);
const LAB_VIDEO_BY_ID = Object.freeze(Object.fromEntries(LAB_VIDEOS.map((item) => [item.id, item])));

/** La vidéo à montrer : sa version établissement pour un parc de catégorie Laboratoire, sinon elle-même. */
export const videoForVariant = (item, variant = getWelfareVariant()) => (item && variant === "lab" ? LAB_VIDEO_BY_ID[item.id] || item : item);

/** Version française pour une interface en français, anglaise pour toutes les autres. */
export const videoLanguage = (language) => (/^fr(?:[-_]|$)/i.test(String(language || "")) ? "fr" : "en");

/**
 * Adresse du lecteur Vimeo. `dnt=1` : Vimeo ne dépose pas de traceur.
 * La lecture démarre seulement quand la personne a cliqué (`autoplay`).
 */
export const videoPlayerUrl = (item, language, { autoplay = false } = {}) => {
  const source = item[videoLanguage(language)];
  const params = new URLSearchParams({ h: source.h, dnt: "1" });
  if (autoplay) params.set("autoplay", "1");
  return `https://player.vimeo.com/video/${source.id}?${params.toString()}`;
};

/** Vignette de la vidéo : son carton de titre (960 × 540), servi par l'app. */
export const videoThumbnailUrl = (item, language) => `/img/onboarding-videos/${item[videoLanguage(language)].id}.jpg`;
